import { format, getMonth, getYear } from 'date-fns';
import utcToZonedTime from 'date-fns-tz/utcToZonedTime';
import { compose } from 'ramda';

import config from 'config';
import bankAccountLocales from 'common/bankAccount/locales';
import { getBankAccountDisplayName } from 'common/bankAccount/utils';
import { capitalizeFirstLetter } from 'helpers/string';

import locales from './locales';
import {
  BankAccount,
  BankStatement,
  BankStatementSectionItem,
  BankStatementsSection,
  FeeStatement,
  Statement,
  StatementTypes,
} from './types';

const timeZone = 'Europe/Paris';

/**
 * @param {Date} startDate - A Date object
 * @return {string} A localized string including month & year, with the timezone
 * "forced" to Paris timezone to ensure the correctness of the date whatever
 * your current location is
 * @example formatSectionItemTitle(new Date('2019-09-05T08:11:45.933Z')) // 'septembre 2019'
 */
export const getLocalizedDate = (startDate: Date) =>
  startDate.toLocaleString(undefined, {
    month: 'long',
    year: 'numeric',
  });

export const formatSectionItemTitle = compose(
  capitalizeFirstLetter,
  getLocalizedDate,
);

const isBankStatement = (
  statement: Statement,
  type: StatementTypes,
): statement is BankStatement => type === StatementTypes.BankStatement;

const isFeeStatement = (
  statement: Statement,
  type: StatementTypes,
): statement is FeeStatement => type === StatementTypes.FeeStatement;

const getStatementType = (statement: Statement) => {
  if (statement.__typename === 'FeeStatement') {
    return StatementTypes.FeeStatement;
  }

  if (statement.__typename === 'BankAccountStatements') {
    return StatementTypes.BankStatement;
  }

  throw new Error('Statement type not supported');
};

const getSectionItem = (
  statement: Statement,
  type: StatementTypes,
  bankAccounts: BankAccount[],
): BankStatementSectionItem => {
  if (isBankStatement(statement, type)) {
    const startDate = utcToZonedTime(new Date(statement.startsAt), timeZone);
    const year = getYear(startDate);
    const month = `0${getMonth(startDate)}`.slice(-2); // add leading zero to month if needed

    const downloadUrl = `${config.shineApiHost}/bank/accounts/statements/${statement.bankAccountStatementId}/export/pdf?accessToken=${statement.accessToken}`;
    const bankAccount = bankAccounts.find(
      ({ bankAccountId }) => bankAccountId === statement.bankAccountId,
    );

    const bankAccountName = bankAccount
      ? getBankAccountDisplayName(bankAccount)
      : bankAccountLocales.mainAccount;
    return {
      description: bankAccountName,
      downloadUrl,
      icon: 'receipt',
      key: statement.bankAccountStatementId,
      sortingDate: `${year}-${month}-01`,
      title: locales.formatString(
        locales.bankStatement,
        formatSectionItemTitle(startDate),
      ) as string,
    };
  }

  if (isFeeStatement(statement, type)) {
    return {
      downloadUrl: statement.signedUrl,
      icon: 'receipt',
      key: statement.feeStatementId,
      sortingDate: `${statement.year}-13-01`,
      title: locales.formatString(
        locales.feeStatementTitle,
        statement.year,
      ) as string,
    };
  }

  throw new Error('Statement type not supported');
};

const getYearSection = (statement: Statement, type: StatementTypes): string => {
  if (isBankStatement(statement, type)) {
    return format(new Date(statement.startsAt), 'yyyy');
  }
  if (isFeeStatement(statement, type)) {
    return statement.year.toString();
  }

  throw new Error('Statement type not supported');
};

/**
 * Map bank and fee statements into a section list (by year)
 * @param bankStatements
 * @param feeStatements
 * @returns Array of sections [ { key: "2021", title: "2021", data: statements }, { key: "2020", title: "2020", data: statements } ]
 */
export const mapStatementsToSectionList = (
  bankStatements: BankStatement[],
  feeStatements: FeeStatement[],
  bankAccounts: BankAccount[],
): BankStatementsSection[] => {
  const statements: Statement[] = [...bankStatements, ...feeStatements];

  const sections = statements.reduce(
    (
      acc: BankStatementsSection[],
      statement: Statement,
    ): BankStatementsSection[] => {
      const statementType = getStatementType(statement);
      const item = getSectionItem(statement, statementType, bankAccounts);
      const yearSection = getYearSection(statement, statementType);
      const sectionIndex = acc.findIndex(({ key }) => key === yearSection);
      const hasFoundExistingSection = sectionIndex > -1;

      if (hasFoundExistingSection) {
        const section = acc[sectionIndex] as BankStatementsSection;

        // Insert document into the right section
        return [
          ...acc.slice(0, sectionIndex),
          {
            ...section,
            data: [...section.data, item].sort((first, second) =>
              first.sortingDate > second.sortingDate ? -1 : 0,
            ),
          },
          ...acc.slice(sectionIndex + 1),
        ];
      }

      // Do not display fee statement if bank statements from a section are not fetched yet
      // Because bank statements are paginated
      if (statementType === StatementTypes.BankStatement) {
        return [...acc, { data: [item], key: yearSection, title: yearSection }];
      }
      return acc;
    },
    [],
  );

  return sections;
};

export const BANK_STATEMENT_LIMIT = 100;
